const SEVERITY_STYLES = {
  critical: 'border-red-400 bg-red-50 text-red-800',
  warning:  'border-yellow-400 bg-yellow-50 text-yellow-800',
  info:     'border-blue-400 bg-blue-50 text-blue-800',
};

/**
 * Transient alert notifications, bottom-right.
 *
 * Renders only what `visibleToasts()` allows through and summarises the rest as
 * a count. Previously this mapped the whole queue into a flex column, so an
 * alert burst stacked cards up the viewport, unreadable, and they scrolled past
 * and vanished on a timer — no notification and no record.
 *
 * A repeated alert carries a `count` badge instead of a card per occurrence:
 * "this happened 37 times" is the operator-useful form, and the alert list is
 * where the detail lives.
 */
export default function Toast({ visible, hiddenCount = 0, onDismiss }) {
  if (!visible || visible.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      role="status"
      aria-live="polite"
    >
      {visible.map((toast) => {
        const style = SEVERITY_STYLES[toast.severity] || SEVERITY_STYLES.info;
        return (
          <div
            key={toast.id}
            className={`flex items-center gap-3 rounded-lg border-l-4 px-4 py-3 shadow-lg ${style}`}
          >
            <span className="flex-1 text-sm">{toast.message}</span>
            {toast.count > 1 && (
              <span
                className="rounded-full bg-current/10 px-2 py-0.5 text-xs font-semibold tabular-nums"
                title={`Repeated ${toast.count} times`}
              >
                ×{toast.count}
              </span>
            )}
            <button
              onClick={() => onDismiss(toast.id)}
              className="text-current opacity-50 hover:opacity-100"
              aria-label="Dismiss notification"
            >
              ✕
            </button>
          </div>
        );
      })}

      {hiddenCount > 0 && (
        <div className="rounded-lg border-l-4 border-slate-300 bg-slate-50 px-4 py-2 text-xs text-slate-600 shadow">
          +{hiddenCount} more {hiddenCount === 1 ? 'alert' : 'alerts'} — see Alerts
        </div>
      )}
    </div>
  );
}
